import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../data/supabaseClient';
import { useFlowRow } from './useFlowRow';
import { actionsFor, cardFor, isTerminal } from './flowGraph';
import { rideAdapter } from './adapters/rideAdapter';
import { packageAdapter } from './adapters/packageAdapter';
import FlowStatusBar from './FlowStatusBar';
import FlowSheet from './FlowSheet';
import { CARD_COMPONENTS } from './cards';
import ReviewCard from './cards/ReviewCard';
import { captureGeoproof } from './geoproof';

const ADAPTERS = { ride: rideAdapter, package: packageAdapter };
const ROLE_VAR = { ride: '--color-role-ride', package: '--color-role-package' };
// status -> stepper index (0 meeting, 1 arrival, 2 hand-off)
const STEP_INDEX = {
  requested: 0,
  confirmed: 0,
  meeting_proposed: 0,
  meeting_agreed: 0,
  waiting: 1,
  both_arrived: 1,
  both_present: 1,
  disputed_no_show: 1,
  handoff: 2,
};

/**
 * Generic orchestrator for the ride/package action flow. Reads the active flow row
 * + graph + adapter, renders the pinned FlowStatusBar and (on tap) a FlowSheet with
 * the current step card, and routes every mutation through the race-safe advance_flow
 * RPC with an optimistic lock. Package pickup handoff redirects to the delivery leg.
 */
export default function FlowEngine({ entity, conversationId, ownerId, travelDate, userId, onError }) {
  const { t } = useTranslation('common');
  const adapter = ADAPTERS[entity];
  const { row, loading, refetch } = useFlowRow(entity, conversationId);
  const [busy, setBusy] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  const dispatch = useCallback(
    async (action, extra = {}) => {
      if (!row || busy || !adapter) return;
      setBusy(true);
      try {
        let effectiveAction = action;
        let toStatus = null;
        let currentLeg = row.current_leg;

        const nextLeg = action === 'confirm_handoff' ? adapter.nextLegOn(row) : null;
        if (nextLeg) {
          effectiveAction = nextLeg.action; // 'advance_leg'
          toStatus = nextLeg.status; // 'meeting_proposed'
          currentLeg = nextLeg.current_leg; // 'delivery'
        } else {
          const ctx = adapter.contextFor(row, userId, { ownerId, travelDate });
          // An action can have multiple transitions from one state (e.g.
          // confirm_presence: self-loop vs. complete) — pick the guard-satisfied one.
          const candidates = actionsFor(row.status, ctx).filter((a) => a.action === action);
          const match = candidates.find((a) => a.guardOk) || candidates[0];
          toStatus = match ? match.to : null;
        }
        if (!toStatus) return;

        // Advisory location proof on the "I'm here" action (never blocks — a
        // denied/unavailable capture still resolves and the action proceeds).
        let geo;
        if (action === 'mark_here') {
          geo = await captureGeoproof();
        }

        const payload = adapter.payloadFor(action, { userId, row, now: new Date().toISOString(), geo, ...extra });

        const { data, error } = await supabase.rpc('advance_flow', {
          p_entity: adapter.entity,
          p_id: row.id,
          p_action: effectiveAction,
          p_new_status: toStatus,
          p_expected_updated_at: row.updated_at,
          p_current_leg: currentLeg,
          p_payload: payload,
        });

        if (error) {
          if (onError) onError(error);
        } else if (data && data.error) {
          if (onError) onError(data);
          if (data.error === 'stale') refetch();
        } else {
          setSheetOpen(false);
          refetch();
        }
      } finally {
        setBusy(false);
      }
    },
    [row, busy, adapter, userId, ownerId, travelDate, refetch, onError]
  );

  const startFlow = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc('create_flow', {
        p_entity: entity,
        p_conversation_id: conversationId,
      });
      if (error) {
        if (onError) onError(error);
      } else if (data && data.error) {
        if (onError) onError(data);
      } else {
        refetch();
      }
    } finally {
      setBusy(false);
    }
  }, [busy, entity, conversationId, refetch, onError]);

  const [reviewed, setReviewed] = useState(false);

  // On a completed flow, check whether the current user has already reviewed.
  useEffect(() => {
    let cancelled = false;
    if (row && row.status === 'completed' && userId) {
      const table = entity === 'ride' ? 'reviews' : 'package_reviews';
      const idCol = entity === 'ride' ? 'ride_id' : 'delivery_id';
      supabase
        .from(table)
        .select('id')
        .eq(idCol, row.id)
        .eq('reviewer_id', userId)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled) setReviewed(!!data);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [row, entity, userId]);

  const submitReview = useCallback(
    async (rating, comment) => {
      if (!row || busy) return;
      const revieweeId =
        entity === 'ride'
          ? userId === row.driver_id
            ? row.passenger_id
            : row.driver_id
          : userId === row.sender_id
            ? row.carrier_id
            : row.sender_id;
      setBusy(true);
      try {
        let error;
        if (entity === 'ride') {
          ({ error } = await supabase.rpc('submit_review_with_notification', {
            p_ride_id: row.id,
            p_reviewer_id: userId,
            p_reviewee_id: revieweeId,
            p_rating: rating,
            p_comment: comment,
          }));
        } else {
          ({ error } = await supabase
            .from('package_reviews')
            .insert({ delivery_id: row.id, reviewer_id: userId, reviewee_id: revieweeId, rating, comment }));
        }
        if (error) {
          if (onError) onError(error);
        } else {
          setReviewed(true);
          setSheetOpen(false);
        }
      } finally {
        setBusy(false);
      }
    },
    [row, busy, entity, userId, onError]
  );

  if (loading || !adapter) return null;

  // Completed flow: prompt for a review until the current user has left one.
  // Once reviewed, fall through to the "start a new flow" branch below so the
  // pair can rebook in the same conversation (create_flow now allows it).
  if (row && row.status === 'completed' && !reviewed) {
    return (
      <>
        <FlowStatusBar
          steps={[]}
          activeIndex={0}
          turnLabel={t('flow.review_prompt')}
          roleColorVar={ROLE_VAR[entity]}
          primaryAction={{ label: t('flow.review_cta'), onClick: () => setSheetOpen(true) }}
          onOpen={() => setSheetOpen(true)}
        />
        <FlowSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t('flow.sheet_review')}>
          <ReviewCard onSubmit={submitReview} busy={busy} roleColorVar={ROLE_VAR[entity]} />
        </FlowSheet>
      </>
    );
  }

  // Reusable "start a new flow" row (only the non-owner requests).
  const canStart = userId && ownerId && userId !== ownerId;
  const startRow = canStart ? (
    <div className="flow-status-bar flow-status-bar--start" style={{ '--flow-role': `var(${ROLE_VAR[entity]})` }}>
      <span className="flow-turn-label">{t('flow.start_body')}</span>
      <button type="button" className="flow-primary-action" disabled={busy} data-action="create_flow" onClick={startFlow}>
        {entity === 'package' ? t('flow.request_package') : t('flow.request_ride')}
      </button>
    </div>
  ) : null;

  // No flow yet, or a completed-and-reviewed flow — offer to start one.
  if (!row || (row.status === 'completed' && reviewed)) {
    return startRow;
  }

  // Other terminal outcomes (cancel / decline / no-show / mutual no-show): show
  // what happened, then let the pair start a new flow (create_flow allows it).
  if (isTerminal(row.status)) {
    const OUTCOME = {
      no_show: { label: t('flow.outcome_no_show'), colorVar: '--color-warning' },
      mutual_no_show: { label: t('flow.outcome_mutual_no_show'), colorVar: '--color-flow-pending' },
      cancelled: { label: t('flow.outcome_cancelled'), colorVar: '--color-flow-pending' },
      declined: { label: t('flow.outcome_declined'), colorVar: '--color-flow-pending' },
    };
    const outcome = OUTCOME[row.status];
    return (
      <>
        {outcome && (
          <FlowStatusBar steps={[]} activeIndex={0} turnLabel={outcome.label} roleColorVar={outcome.colorVar} />
        )}
        {startRow}
      </>
    );
  }

  const ctx = adapter.contextFor(row, userId, { ownerId, travelDate });
  const actions = actionsFor(row.status, ctx).filter((a) => a.action !== 'advance_leg');
  const cardId = cardFor(row.status);
  const CardComponent = CARD_COMPONENTS[cardId];
  const roleColorVar = ROLE_VAR[entity];
  const hasMove = actions.some((a) => a.guardOk);
  const steps = [t('flow.step_meeting'), t('flow.step_arrival'), t('flow.step_handoff')];

  return (
    <>
      <FlowStatusBar
        steps={steps}
        activeIndex={STEP_INDEX[row.status] ?? 0}
        turnLabel={hasMove ? t('flow.your_move') : t('flow.their_move')}
        roleColorVar={roleColorVar}
        primaryAction={hasMove ? { label: t('flow.open'), onClick: () => setSheetOpen(true) } : null}
        onOpen={() => setSheetOpen(true)}
      />
      <FlowSheet open={sheetOpen} onClose={() => setSheetOpen(false)} title={t(`flow.sheet_${cardId}`)}>
        {CardComponent && (
          <CardComponent row={row} actions={actions} dispatch={dispatch} busy={busy} roleColorVar={roleColorVar} />
        )}
      </FlowSheet>
    </>
  );
}
